import { computed, ref, unref, watch, type ComputedRef, type Ref } from "vue";
import type {
  Company,
  SessionInformation,
  VacationPolicy,
} from "@wkt/api/gwt/gwtDTO";
import {
  employeeCalendarAPI,
  type EmployeeCalendar,
} from "@wkt/api/employee-calendar";
import { proceduresApi } from "@wkt/api/procedures";
import type {
  FullProcedureDefinitionDTO,
  FullEmployeeDTO,
  FullProcedureRequestDTO,
  HoursBagDTO,
  VacationDataDTO,
  VacationEntryDTO,
} from "@wkt/api/procedures";
import { useDateConverter } from "@wkt/composables/useDateConverter";
import type { ProcedureRequestDialogMode } from "./procedureRequestDialogContract";

type MaybeRef<T> = T | Ref<T> | ComputedRef<T>;

const VACATION_COUNT_NONE = 0;
const VACATION_COUNT_NATURAL_DAYS = 1;
const VACATION_COUNT_WORK_DAYS = 2;

const HOURS_BAG_1_ID = 1;
const HOURS_BAG_2_ID = 2;
const HOURS_BAG_3_ID = 3;
const HOURS_BAG_4_ID = 4;
const HOURS_BAG_5_ID = 5;
const HOURS_BAG_VACATION_ID = 6;
const HOURS_BAG_EXTRA_ID = 7;

const REQUEST_SUBTYPE_COMPLETE_DAY = 7;
const REQUEST_SUBTYPE_FOR_HOURS = 8;
const REGULAR_DAY_TYPE = 0;

export interface ProcedureVacationDraftLike {
  fromDate: Date | null;
  toDate: Date | null;
  fromTimeText: string;
  toTimeText: string;
  vacationDaysText: string;
  hoursBagType: number | null;
}

export interface UseProcedureVacationSupportParams {
  visible: MaybeRef<boolean>;
  mode: MaybeRef<ProcedureRequestDialogMode>;
  request: MaybeRef<FullProcedureRequestDTO | null | undefined>;
  definition: MaybeRef<FullProcedureDefinitionDTO | null | undefined>;
  draft: MaybeRef<ProcedureVacationDraftLike>;
  sessionInformation: MaybeRef<SessionInformation | null | undefined>;
  approverEmployee: MaybeRef<FullEmployeeDTO | null | undefined>;
}

export function useProcedureVacationSupport(params: UseProcedureVacationSupportParams) {
  const { convertDateToSimpleDate, compareSimpleDates } = useDateConverter();

  const vacationData = ref<VacationDataDTO | null>(null);
  const pendingApprovedHoursBags = ref<HoursBagDTO[]>([]);
  const calendarDays = ref<EmployeeCalendar[]>([]);

  const balancesLoading = ref(false);
  const calendarLoading = ref(false);
  const missingCalendarConfiguration = ref(false);

  let balancesRequestToken = 0;
  let calendarRequestToken = 0;

  const visible = computed(() => Boolean(unref(params.visible)));
  const mode = computed(() => unref(params.mode));
  const request = computed(() => unref(params.request) || null);
  const definition = computed(() => unref(params.definition) || null);
  const draft = computed(() => unref(params.draft));
  const sessionInformation = computed(() => unref(params.sessionInformation) || null);
  const approverEmployee = computed(() => unref(params.approverEmployee) || null);

  const employeeId = computed(
    () => request.value?.employeeId ?? sessionInformation.value?.employee?.id ?? null
  );
  const currentEmployeeId = computed(() => sessionInformation.value?.employee?.id ?? null);

  const isVacationLeave = computed(
    () => definition.value?.procedureClass === "VACATION_LEAVE"
  );

  const isVacationByDays = computed(
    () =>
      isVacationLeave.value &&
      definition.value?.requestSubtype === REQUEST_SUBTYPE_COMPLETE_DAY
  );

  const isVacationByHours = computed(
    () =>
      isVacationLeave.value &&
      definition.value?.requestSubtype === REQUEST_SUBTYPE_FOR_HOURS
  );

  const isOwnRequest = computed(
    () =>
      employeeId.value != null &&
      currentEmployeeId.value != null &&
      employeeId.value === currentEmployeeId.value
  );

  const selectedHoursBagType = computed(() =>
    definition.value?.showVacationDays ? draft.value.hoursBagType : null
  );

  const assistedCountMode = computed(() =>
    getAssistedVacationCountMode(
      sessionInformation.value?.company || null,
      sessionInformation.value?.employeeVacationPolicy || null
    )
  );

  const effectiveCountMode = computed(() => {
    if (selectedHoursBagType.value != null && selectedHoursBagType.value !== HOURS_BAG_VACATION_ID) {
      return VACATION_COUNT_WORK_DAYS;
    }

    return assistedCountMode.value;
  });

  const showManualDaysInput = computed(
    () =>
      isVacationByDays.value &&
      Boolean(definition.value?.showVacationDays) &&
      assistedCountMode.value === VACATION_COUNT_NONE
  );

  const manualDaysRequested = computed(() => {
    if (!showManualDaysInput.value) {
      return null;
    }

    if (mode.value === "approve" && request.value?.hoursBagDelta != null) {
      return roundTo2Decimals(Math.abs(request.value.hoursBagDelta));
    }

    const normalized = normalizeNumberString(draft.value.vacationDaysText);
    return normalized == null ? null : roundTo2Decimals(Math.abs(normalized));
  });

  const requestedHours = computed(() =>
    computeRequestedHours(draft.value.fromTimeText, draft.value.toTimeText)
  );

  const workdayHours = computed(() => {
    if (!isVacationByHours.value) {
      return null;
    }

    if (definition.value?.hoursReference === 1) {
      return normalizeNumber(definition.value.defaultQuantity);
    }

    if (definition.value?.hoursReference === 0) {
      const requestDate = convertDateToSimpleDate(draft.value.fromDate);
      const calendarDay = requestDate
        ? calendarDays.value.find((entry) =>
            compareSimpleDates(parseCalendarSimpleDate(entry.calendarDay), requestDate) === 0
          )
        : null;

      return normalizeNumber(calendarDay?.workdayHours);
    }

    return null;
  });

  const requestDayEquivalent = computed(() => {
    if (!isVacationLeave.value) {
      return null;
    }

    if (isVacationByDays.value) {
      if (showManualDaysInput.value) {
        return manualDaysRequested.value;
      }

      return computeVacationDaysFromCalendar(
        calendarDays.value,
        draft.value.fromDate,
        draft.value.toDate,
        effectiveCountMode.value
      );
    }

    if (isVacationByHours.value) {
      const hoursRequested = requestedHours.value;
      const referenceHours = workdayHours.value;

      if (hoursRequested == null || referenceHours == null || referenceHours <= 0) {
        return null;
      }

      return roundTo2Decimals(hoursRequested / referenceHours);
    }

    return null;
  });

  const balanceRequestDays = computed(() => {
    if (!definition.value?.showVacationDays) {
      return null;
    }

    if (mode.value === "approve" && request.value?.hoursBagDelta != null) {
      return roundTo2Decimals(request.value.hoursBagDelta * -1);
    }

    return requestDayEquivalent.value;
  });

  const submitHoursBagDelta = computed(() => {
    if (!isVacationLeave.value) {
      return null;
    }

    if (isVacationByHours.value && !definition.value?.showVacationDays) {
      return 0;
    }

    if (requestDayEquivalent.value == null) {
      return null;
    }

    return roundTo2Decimals(requestDayEquivalent.value * -1);
  });

  const submitQuantity = computed(() => {
    if (isVacationByHours.value) {
      return requestedHours.value;
    }

    return null;
  });

  const currentBalance = computed(() => {
    if (!definition.value?.showVacationDays || selectedHoursBagType.value == null) {
      return null;
    }

    if (mode.value === "approve") {
      return getEmployeeCurrentBalance(
        approverEmployee.value,
        selectedHoursBagType.value
      );
    }

    const lastVacation = findLastVacationByBag(
      vacationData.value?.vacationData || [],
      selectedHoursBagType.value
    );

    return roundTo2Decimals(lastVacation?.hoursBagTotal ?? 0);
  });

  const requestsPending = computed(() => {
    if (!definition.value?.showVacationDays || selectedHoursBagType.value == null) {
      return null;
    }

    const hoursBag = pendingApprovedHoursBags.value.find(
      (item) => item?.id === selectedHoursBagType.value
    );

    return roundTo2Decimals(hoursBag?.days ?? 0);
  });

  const requestCurrent = computed(() => {
    if (!definition.value?.showVacationDays) {
      return null;
    }

    if (mode.value === "approve" && request.value?.hoursBagDelta != null) {
      return roundTo2Decimals(request.value.hoursBagDelta);
    }

    if (requestDayEquivalent.value == null) {
      return null;
    }

    return roundTo2Decimals(requestDayEquivalent.value * -1);
  });

  const finalBalance = computed(() => {
    if (
      !definition.value?.showVacationDays ||
      currentBalance.value == null ||
      requestsPending.value == null ||
      balanceRequestDays.value == null
    ) {
      return null;
    }

    return roundTo2Decimals(
      currentBalance.value + (requestsPending.value - balanceRequestDays.value)
    );
  });

  const allowNegative = computed(() =>
    resolveAllowNegative(sessionInformation.value?.company || null, selectedHoursBagType.value)
  );

  const negativeBalanceWarning = computed(
    () =>
      Boolean(definition.value?.showVacationDays) &&
      finalBalance.value != null &&
      finalBalance.value < 0
  );

  const balanceInsufficient = computed(
    () =>
      negativeBalanceWarning.value &&
      !allowNegative.value
  );

  const showOwnCalendarAction = computed(
    () => visible.value && isVacationLeave.value && mode.value !== "approve"
  );

  const showVacationBalance = computed(
    () =>
      visible.value &&
      isVacationLeave.value &&
      Boolean(definition.value?.showVacationDays) &&
      !showManualDaysInput.value
  );

  watch(
    [
      visible,
      isVacationLeave,
      isOwnRequest,
      () => request.value?.id ?? null,
      employeeId,
      () => definition.value?.showVacationDays ?? false,
      selectedHoursBagType,
      mode,
    ],
    ([isVisible, vacationLeave, ownRequest, requestId, resolvedEmployeeId, showVacationDays]) => {
      balancesRequestToken += 1;
      const currentToken = balancesRequestToken;
      let vacationDataResolved = !ownRequest;
      let pendingApprovedResolved = false;

      if (!isVisible || !vacationLeave || !showVacationDays || resolvedEmployeeId == null) {
        vacationData.value = null;
        pendingApprovedHoursBags.value = [];
        balancesLoading.value = false;
        return;
      }

      balancesLoading.value = true;

      const finalizeLoading = () => {
        if (currentToken !== balancesRequestToken) {
          return;
        }

        if (vacationDataResolved && pendingApprovedResolved) {
          balancesLoading.value = false;
        }
      };

      if (ownRequest) {
        proceduresApi.getVacationData(
          (response) => {
            if (currentToken !== balancesRequestToken) {
              return;
            }

            vacationData.value = response || null;
            vacationDataResolved = true;
            finalizeLoading();
          },
          () => {
            if (currentToken !== balancesRequestToken) {
              return;
            }

            vacationData.value = null;
            vacationDataResolved = true;
            finalizeLoading();
          }
        );
      } else {
        vacationData.value = null;
      }

      proceduresApi.getSumRequestPendingApproved(
        resolvedEmployeeId,
        requestId ?? -1,
        (response) => {
          if (currentToken !== balancesRequestToken) {
            return;
          }

          pendingApprovedHoursBags.value = response || [];
          pendingApprovedResolved = true;
          finalizeLoading();
        },
        () => {
          if (currentToken !== balancesRequestToken) {
            return;
          }

          pendingApprovedHoursBags.value = [];
          pendingApprovedResolved = true;
          finalizeLoading();
        }
      );
    },
    { immediate: true }
  );

  watch(
    [
      visible,
      isVacationLeave,
      isVacationByDays,
      isVacationByHours,
      mode,
      () => definition.value?.hoursReference ?? null,
      effectiveCountMode,
      () => draft.value.fromDate?.getTime() ?? null,
      () => draft.value.toDate?.getTime() ?? null,
      () => draft.value.fromTimeText,
      () => draft.value.toTimeText,
    ],
    ([
      isVisible,
      vacationLeave,
      vacationByDays,
      vacationByHours,
      currentMode,
      hoursReference,
      countMode,
      fromDateTime,
      toDateTime,
    ]) => {
      calendarRequestToken += 1;
      const currentToken = calendarRequestToken;

      missingCalendarConfiguration.value = false;

      if (!isVisible || !vacationLeave) {
        calendarDays.value = [];
        calendarLoading.value = false;
        return;
      }

      if (currentMode === "approve") {
        calendarDays.value = [];
        calendarLoading.value = false;
        return;
      }

      const fromDate = fromDateTime != null ? new Date(fromDateTime) : null;
      const toDate = toDateTime != null ? new Date(toDateTime) : null;

      if (vacationByHours) {
        if (!fromDate) {
          calendarDays.value = [];
          calendarLoading.value = false;
          return;
        }

        if (hoursReference !== 0) {
          calendarDays.value = [];
          calendarLoading.value = false;
          return;
        }

        calendarLoading.value = true;
        employeeCalendarAPI.getCalendar(
          {
            fromDate: convertDateToSimpleDate(fromDate)!,
            toDate: convertDateToSimpleDate(fromDate)!,
          },
          (response) => {
            if (currentToken !== calendarRequestToken) {
              return;
            }

            calendarDays.value = response || [];
            calendarLoading.value = false;
          },
          () => {
            if (currentToken !== calendarRequestToken) {
              return;
            }

            calendarDays.value = [];
            calendarLoading.value = false;
          }
        );
        return;
      }

      if (!vacationByDays || !fromDate || !toDate || countMode === VACATION_COUNT_NONE) {
        calendarDays.value = [];
        calendarLoading.value = false;
        return;
      }

      const calendarToDate = new Date(toDate);
      if (countMode === VACATION_COUNT_NATURAL_DAYS) {
        calendarToDate.setDate(calendarToDate.getDate() + 5);
      }

      calendarLoading.value = true;
      employeeCalendarAPI.getCalendar(
        {
          fromDate: convertDateToSimpleDate(fromDate)!,
          toDate: convertDateToSimpleDate(calendarToDate)!,
        },
        (response) => {
          if (currentToken !== calendarRequestToken) {
            return;
          }

          calendarDays.value = response || [];
          calendarLoading.value = false;
        },
        () => {
          if (currentToken !== calendarRequestToken) {
            return;
          }

          calendarDays.value = [];
          calendarLoading.value = false;
        }
      );
    },
    { immediate: true }
  );

  watch(
    [isVacationByHours, mode, () => definition.value?.hoursReference ?? null, requestedHours, workdayHours],
    ([vacationByHours, currentMode, hoursReference, hoursRequested, resolvedWorkdayHours]) => {
      if (
        currentMode === "approve" ||
        !vacationByHours ||
        hoursReference !== 0 ||
        hoursRequested == null
      ) {
        missingCalendarConfiguration.value = false;
        return;
      }

      missingCalendarConfiguration.value =
        resolvedWorkdayHours == null || resolvedWorkdayHours <= 0;
    },
    { immediate: true }
  );

  return {
    isVacationLeave,
    isVacationByDays,
    isVacationByHours,
    showOwnCalendarAction,
    showVacationBalance,
    balancesLoading,
    calendarLoading,
    requestedHours,
    workdayHours,
    requestDayEquivalent,
    showManualDaysInput,
    manualDaysRequested,
    submitHoursBagDelta,
    submitQuantity,
    currentBalance,
    requestsPending,
    requestCurrent,
    finalBalance,
    allowNegative,
    negativeBalanceWarning,
    balanceInsufficient,
    missingCalendarConfiguration,
    effectiveCountMode,
  };
}

function getAssistedVacationCountMode(
  company: Company | null,
  vacationPolicy: VacationPolicy | null
): number {
  if (vacationPolicy?.assistedCountMode != null) {
    return vacationPolicy.assistedCountMode;
  }

  return company?.assistedVacationCountMode ?? VACATION_COUNT_NONE;
}

function getEmployeeCurrentBalance(
  employee: FullEmployeeDTO | null,
  hoursBagType?: number | null
): number | null {
  if (!employee || hoursBagType == null) {
    return null;
  }

  switch (hoursBagType) {
    case HOURS_BAG_1_ID:
      return normalizeNumber(employee.currentHoursBag1Days);
    case HOURS_BAG_2_ID:
      return normalizeNumber(employee.currentHoursBag2Days);
    case HOURS_BAG_3_ID:
      return normalizeNumber(employee.currentHoursBag3Days);
    case HOURS_BAG_4_ID:
      return normalizeNumber(employee.currentHoursBag4Days);
    case HOURS_BAG_5_ID:
      return normalizeNumber(employee.currentHoursBag5Days);
    case HOURS_BAG_VACATION_ID:
      return normalizeNumber(employee.currentVacationDays);
    case HOURS_BAG_EXTRA_ID:
      return normalizeNumber(employee.currentExtraDays);
    default:
      return null;
  }
}

function computeRequestedHours(
  fromTimeText?: string | null,
  toTimeText?: string | null
): number | null {
  const fromTime = parseTimeText(fromTimeText);
  const toTime = parseTimeText(toTimeText);

  if (!fromTime || !toTime) {
    return null;
  }

  const fromMinutes = fromTime.hours * 60 + fromTime.minutes;
  const toMinutes = toTime.hours * 60 + toTime.minutes;

  if (toMinutes <= fromMinutes) {
    return null;
  }

  return roundTo2Decimals((toMinutes - fromMinutes) / 60);
}

function normalizeNumberString(value?: string | null): number | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }

  const normalized = Number(trimmed.replace(",", "."));
  return Number.isFinite(normalized) ? normalized : null;
}

function parseTimeText(value?: string | null): { hours: number; minutes: number } | null {
  if (!value?.trim()) {
    return null;
  }

  const match = value.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);

  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return { hours, minutes };
}

function computeVacationDaysFromCalendar(
  calendar: EmployeeCalendar[],
  fromDate: Date | null,
  toDate: Date | null,
  countMode: number
): number | null {
  if (!fromDate || !toDate || countMode === VACATION_COUNT_NONE || !calendar.length) {
    return null;
  }

  const sortedCalendar = [...calendar].sort((left, right) => {
    const leftTime = parseCalendarDateToTime(left.calendarDay);
    const rightTime = parseCalendarDateToTime(right.calendarDay);
    return leftTime - rightTime;
  });

  const dayBack = new Date(toDate);
  dayBack.setDate(dayBack.getDate() + 1);
  const dayBackSimple = {
    year: dayBack.getFullYear(),
    month: dayBack.getMonth() + 1,
    day: dayBack.getDate(),
  };

  let firstWorkdayFound = false;
  let days = 0;

  for (const entry of sortedCalendar) {
    const calendarDay = parseCalendarSimpleDate(entry.calendarDay);
    if (!calendarDay) {
      continue;
    }

    if (!firstWorkdayFound && entry.dayType === REGULAR_DAY_TYPE) {
      firstWorkdayFound = true;
    }

    if (!firstWorkdayFound) {
      continue;
    }

    if (entry.countsAsVacation === false) {
      continue;
    }

    if (countMode === VACATION_COUNT_NATURAL_DAYS) {
      if (comparePlainSimpleDates(calendarDay, dayBackSimple) > 0 && entry.dayType === REGULAR_DAY_TYPE) {
        break;
      }
      days += 1;
      continue;
    }

    if (countMode === VACATION_COUNT_WORK_DAYS) {
      if (entry.dayType === REGULAR_DAY_TYPE && comparePlainSimpleDates(calendarDay, dayBackSimple) <= 0) {
        days += 1;
      }
    }
  }

  return roundTo2Decimals(days);
}

function parseCalendarSimpleDate(value?: string | null) {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
}

function parseCalendarDateToTime(value?: string | null): number {
  if (!value) {
    return 0;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function comparePlainSimpleDates(
  left: { year: number; month: number; day: number } | null,
  right: { year: number; month: number; day: number } | null
): number {
  if (!left && !right) {
    return 0;
  }

  if (!left) {
    return 1;
  }

  if (!right) {
    return -1;
  }

  if (left.year !== right.year) {
    return left.year - right.year;
  }

  if (left.month !== right.month) {
    return left.month - right.month;
  }

  return left.day - right.day;
}

function findLastVacationByBag(
  vacations: VacationEntryDTO[],
  hoursBagType: number
): VacationEntryDTO | null {
  return (
    vacations.find((vacation) => vacation?.hoursBagType === hoursBagType) || null
  );
}

function resolveAllowNegative(company: Company | null, hoursBagType: number | null): boolean {
  if (company == null || hoursBagType == null) {
    return true;
  }

  switch (hoursBagType) {
    case HOURS_BAG_VACATION_ID:
      return company.defaultYearlyVacationAllowNegative ?? true;
    case HOURS_BAG_EXTRA_ID:
      return company.defaultYearlyExtraAllowNegative ?? true;
    case HOURS_BAG_1_ID:
      return company.defaultYearlyHoursBag1AllowNegative ?? true;
    case HOURS_BAG_2_ID:
      return company.defaultYearlyHoursBag2AllowNegative ?? true;
    case HOURS_BAG_3_ID:
      return company.defaultYearlyHoursBag3AllowNegative ?? true;
    case HOURS_BAG_4_ID:
      return company.defaultYearlyHoursBag4AllowNegative ?? true;
    case HOURS_BAG_5_ID:
      return company.defaultYearlyHoursBag5AllowNegative ?? true;
    default:
      return true;
  }
}

function normalizeNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  return null;
}

function roundTo2Decimals(value: number): number {
  return Math.round(value * 100) / 100;
}
